import React, { useState, useEffect } from 'react';
import { fetchGamesForDate, LIVE_GAMES_ENABLED } from '../services/liveGamesService';

/**
 * GamesBanner - Displays MLB games with live scores for a selected date
 * Shows below the main navbar, full width
 * Includes date navigation to change the date and a favorite-team star/highlight.
 * Always rendered as the "slim" collapsed single-row variant (see the Slim variant
 * in design-system/mlb-scoreboard.html).
 */
const HARDCODED_GAMES = [
  {
    GameID: 1,
    Status: 'Final',
    AwayTeam: 'NYY',
    HomeTeam: 'BOS',
    AwayTeamRuns: 5,
    HomeTeamRuns: 3,
    DateTime: null,
  },
  {
    GameID: 2,
    Status: 'Final',
    AwayTeam: 'LAD',
    HomeTeam: 'SF',
    AwayTeamRuns: 4,
    HomeTeamRuns: 7,
    DateTime: null,
  },
  {
    GameID: 3,
    Status: 'InProgress',
    AwayTeam: 'CHC',
    HomeTeam: 'MIL',
    AwayTeamRuns: 2,
    HomeTeamRuns: 2,
    Inning: 5,
    InningHalf: 'Top',
    Outs: 1,
    RunnerOn1B: true,
    RunnerOn2B: false,
    RunnerOn3B: true,
    DateTime: null,
  },
  {
    GameID: 4,
    Status: 'InProgress',
    AwayTeam: 'HOU',
    HomeTeam: 'SEA',
    AwayTeamRuns: 6,
    HomeTeamRuns: 4,
    Inning: 8,
    InningHalf: 'Bottom',
    Outs: 2,
    RunnerOn1B: true,
    RunnerOn2B: true,
    RunnerOn3B: true,
    DateTime: null,
  },
  {
    GameID: 5,
    Status: 'Scheduled',
    AwayTeam: 'NYM',
    HomeTeam: 'ATL',
    AwayTeamRuns: null,
    HomeTeamRuns: null,
    DateTime: new Date(new Date().setHours(19, 10, 0, 0)).toISOString(),
  },
  {
    GameID: 6,
    Status: 'Scheduled',
    AwayTeam: 'PHI',
    HomeTeam: 'WSH',
    AwayTeamRuns: null,
    HomeTeamRuns: null,
    DateTime: new Date(new Date().setHours(19, 5, 0, 0)).toISOString(),
  },
  {
    GameID: 7,
    Status: 'Scheduled',
    AwayTeam: 'OAK',
    HomeTeam: 'TB',
    AwayTeamRuns: null,
    HomeTeamRuns: null,
    DateTime: new Date(new Date().setHours(13, 5, 0, 0)).toISOString(),
  },
  {
    GameID: 8,
    Status: 'Delayed',
    AwayTeam: 'CLE',
    HomeTeam: 'DET',
    AwayTeamRuns: null,
    HomeTeamRuns: null,
    DateTime: null,
  },
];

const FAVORITE_TEAM_KEY = 'tjfb-favorite-mlb-team';

function GamesBanner() {
  // HARDCODED_GAMES doubles as the runtime fallback shown if the live fetch
  // fails (e.g. missing/invalid SportsData.io API key) - see the catch block
  // in loadGamesForDate below.
  const [games, setGames] = useState(HARDCODED_GAMES);
  // When the live integration is disabled we render the sample data immediately,
  // so there's no "loading" phase to show.
  const [loading, setLoading] = useState(LIVE_GAMES_ENABLED);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Default to today (YYYY-MM-DD)
  const [favoriteTeam, setFavoriteTeamState] = useState(() => {
    try {
      return localStorage.getItem(FAVORITE_TEAM_KEY) || null;
    } catch {
      return null;
    }
  });
  // Get min/max dates for date nav (today and up to 1 year in future)
  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateString = maxDate.toISOString().split('T')[0];

  // loadGamesForDate() delegates to the shared liveGamesService, which routes
  // through the fantasy-data-api Lambda proxy (see fantasyDataService.js) so the
  // SportsData.io key stays server-side. Gated on LIVE_GAMES_ENABLED: while the
  // integration is off we skip polling entirely and keep the sample data that's
  // already in state, rather than firing a request that only ever 401s.
  useEffect(() => {
    if (!LIVE_GAMES_ENABLED) return undefined;
    loadGamesForDate(selectedDate);
    const interval = setInterval(() => {
      loadGamesForDate(selectedDate);
    }, 30000);
    return () => clearInterval(interval);
  }, [selectedDate]);

  const setFavoriteTeam = (abbr) => {
    const next = favoriteTeam === abbr ? null : abbr;
    setFavoriteTeamState(next);
    try {
      if (next) localStorage.setItem(FAVORITE_TEAM_KEY, next);
      else localStorage.removeItem(FAVORITE_TEAM_KEY);
    } catch {
      // localStorage unavailable (private browsing, etc.) - favorite just won't persist.
    }
  };

  // Format date for API (YYYY-MM-DD) - SportsData.io format
  const formatDateForAPI = (dateString) => {
    // dateString is already in YYYY-MM-DD format from input[type="date"]
    return dateString;
  };

  // Format date for display: abbreviated month and day (e.g. "Jul 15")
  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Load games for a specific date from SportsData.io API (via shared liveGamesService).
  const loadGamesForDate = async (dateString) => {
    try {
      setLoading(true);
      setError(null);

      const formattedDate = formatDateForAPI(dateString);
      const regularGames = await fetchGamesForDate(formattedDate);

      setGames(regularGames);

      if (regularGames.length === 0) {
        // No games for this date, or all were postponed/canceled/suspended -
        // this is a legitimate empty state, not an error.
        setError('no-games');
      }

      setLoading(false);
    } catch (err) {
      // Fall back to sample data ONLY on fetch failure (network error, Lambda/API key
      // misconfiguration, rate limit, etc.) so the banner degrades gracefully instead of
      // showing a blank screen. This is NOT live data - if you see this consistently,
      // check the FANTASY_DATA_API_KEY secret on the fantasy-data-api Lambda (see
      // documentation/FANTASY_DATA_API_KEY_SETUP.md).
      console.error('Error loading games, falling back to sample data:', err);
      setError(null);
      setGames(HARDCODED_GAMES);
      setLoading(false);
    }
  };

  // Handle date change from the native date input (opened by clicking the date label)
  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
  };

  // Step the selected date by +/-1 day, clamped to [today, maxDateString]
  const shiftDate = (deltaDays) => {
    const d = new Date(selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + deltaDays);
    const iso = d.toISOString().split('T')[0];
    if (iso < today || iso > maxDateString) return;
    setSelectedDate(iso);
  };

  const formatTime = (dateTimeString) => {
    if (!dateTimeString) return 'TBD';
    const date = new Date(dateTimeString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const inningOrdinal = (n) => {
    if (n == null || n === '') return '';
    const d = Number(n);
    const v = d % 100;
    const suffix = (v >= 11 && v <= 13) ? 'th' : (d % 10 === 1 ? 'st' : d % 10 === 2 ? 'nd' : d % 10 === 3 ? 'rd' : 'th');
    return `${d}${suffix}`;
  };

  const getGameStatus = (game) => {
    // Game statuses from Fantasy Data API:
    // Scheduled, InProgress, Final, F/OT, Suspended, Postponed, Delayed, Canceled
    const status = game.Status;

    if (status === 'Scheduled') {
      return {
        type: 'scheduled',
        text: formatTime(game.DateTime)
      };
    } else if (status === 'InProgress') {
      return {
        type: 'live',
        inning: game.Inning,
        isBottom: game.InningHalf ? String(game.InningHalf).toLowerCase().startsWith('b') : false,
        outs: typeof game.Outs === 'number' ? game.Outs : null,
        runnerOn1B: game.RunnerOn1B,
        runnerOn2B: game.RunnerOn2B,
        runnerOn3B: game.RunnerOn3B
      };
    } else if (status === 'Final' || status === 'F/OT') {
      return {
        type: 'final',
        text: 'Final'
      };
    } else if (status === 'Postponed' || status === 'Delayed') {
      return {
        type: 'postponed',
        text: status.toUpperCase()
      };
    } else {
      return {
        type: 'scheduled',
        text: formatTime(game.DateTime)
      };
    }
  };

  // A team abbreviation the user can click to set/unset as their favorite
  // team - styled to look like plain text until hovered.
  const TeamButton = ({ abbr }) => (
    <button
      type="button"
      className="games-banner-team-btn"
      onClick={(e) => {
        e.stopPropagation();
        setFavoriteTeam(abbr);
      }}
      title={favoriteTeam === abbr ? `Remove ${abbr} as favorite team` : `Set ${abbr} as favorite team`}
    >
      {abbr}
    </button>
  );

  // Single-line "AWAY R · HOME R" score matching design-system/mlb-scoreboard.html's
  // .games-banner-game, with per-team dimming for the loser once a game is Final,
  // a favorite-team star, and an inning-direction arrow (Top = up, Bottom = down)
  // in place of the old live-dot + text treatment.
  const getScoreDisplay = (game, statusArg) => {
    const status = statusArg || getGameStatus(game);
    const hasScore = status.type === 'live' || status.type === 'final';
    const isFav = !!favoriteTeam && (game.AwayTeam === favoriteTeam || game.HomeTeam === favoriteTeam);

    const awayWins = status.type === 'final' && (game.AwayTeamRuns ?? 0) > (game.HomeTeamRuns ?? 0);
    const homeWins = status.type === 'final' && (game.HomeTeamRuns ?? 0) > (game.AwayTeamRuns ?? 0);

    return (
      <>
        <div className="games-banner-score">
          {isFav && <span className="games-banner-fav-star">★</span>}
          {hasScore ? (
            <>
              <span className={status.type === 'final' && !awayWins ? 'dim' : undefined}>
                <TeamButton abbr={game.AwayTeam} /> {game.AwayTeamRuns ?? 0}
              </span>
              {' · '}
              <span className={status.type === 'final' && !homeWins ? 'dim' : undefined}>
                <TeamButton abbr={game.HomeTeam} /> {game.HomeTeamRuns ?? 0}
              </span>
            </>
          ) : (
            <>
              <TeamButton abbr={game.AwayTeam} /> @ <TeamButton abbr={game.HomeTeam} />
            </>
          )}
        </div>
        <div className="games-banner-cardrow">
          {status.type === 'live' ? (
            <div className="games-banner-situation">
              <div className="games-banner-diamond-group">
                <div className="games-banner-bases">
                  <span className={`games-banner-base b2${status.runnerOn2B ? ' on' : ''}`} />
                  <span className={`games-banner-base b1${status.runnerOn1B ? ' on' : ''}`} />
                  <span className={`games-banner-base b3${status.runnerOn3B ? ' on' : ''}`} />
                </div>
                {status.outs != null && (
                  <div className="games-banner-outs">
                    {[0, 1, 2].map((i) => (
                      <span key={i} className={`games-banner-out${i < status.outs ? ' on' : ''}`} />
                    ))}
                  </div>
                )}
              </div>
              <div className="games-banner-inning">
                <svg viewBox="0 0 10 10">
                  <polygon points={status.isBottom ? '1,2 9,2 5,9' : '5,1 9,8 1,8'} />
                </svg>
                <span>{inningOrdinal(status.inning)}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="games-banner-situation" />
              <span className={`games-banner-state games-banner-state-${status.type}`}>{status.text}</span>
            </>
          )}
        </div>
      </>
    );
  };

  const isToday = selectedDate === today;

  const DateColumn = () => (
    <div className="games-banner-date">
      <div className="games-banner-date-nav">
        <button
          type="button"
          className="games-banner-date-btn"
          onClick={() => shiftDate(-1)}
          disabled={isToday}
          aria-label="Previous day"
        >
          ‹
        </button>
        <label className="games-banner-date-label-wrap">
          <span className="games-banner-date-label">{formatDateForDisplay(selectedDate)}</span>
          <input
            type="date"
            value={selectedDate}
            onChange={handleDateChange}
            min={today}
            max={maxDateString}
            className="games-banner-date-input"
            aria-label="Select date"
          />
        </label>
        <button
          type="button"
          className="games-banner-date-btn"
          onClick={() => shiftDate(1)}
          disabled={selectedDate === maxDateString}
          aria-label="Next day"
        >
          ›
        </button>
      </div>
      {isToday && <span className="games-banner-date-today">Today</span>}
    </div>
  );

  if (loading) {
    return (
      <div className="games-banner-header">
        <div className="games-banner-status-row">
          <DateColumn />
          <div className="games-banner-status">Loading games...</div>
        </div>
      </div>
    );
  }

  // Show error message for API errors (but not for "no games")
  if (error && error !== 'no-games') {
    return (
      <div className="games-banner-header">
        <div className="games-banner-status-row">
          <DateColumn />
          <div className="games-banner-status" style={{ color: 'var(--danger)' }}>
            Error: {error}
          </div>
        </div>
      </div>
    );
  }

  // Show "no games" message when there are no games for the day
  if (error === 'no-games' || (!loading && games.length === 0)) {
    return (
      <div className="games-banner-header">
        <div className="games-banner-status-row">
          <DateColumn />
          <div className="games-banner-status">No games scheduled</div>
        </div>
      </div>
    );
  }

  // Favorite team's game(s) always sort first, otherwise preserve API order.
  const sortedGames = favoriteTeam
    ? [...games].sort((a, b) => {
        const aFav = a.AwayTeam === favoriteTeam || a.HomeTeam === favoriteTeam;
        const bFav = b.AwayTeam === favoriteTeam || b.HomeTeam === favoriteTeam;
        return aFav === bFav ? 0 : aFav ? -1 : 1;
      })
    : games;

  return (
    <div className="games-banner-header">
      <div className="games-banner-wrap">
        <div className="games-banner-ticker is-slim">
          <DateColumn />
          {sortedGames.map((game, index) => {
            const status = getGameStatus(game);
            const isFav = !!favoriteTeam && (game.AwayTeam === favoriteTeam || game.HomeTeam === favoriteTeam);
            return (
              <div key={game.GameID || index} className={`games-banner-game${isFav ? ' is-fav' : ''}`}>
                {getScoreDisplay(game, status)}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default GamesBanner;
